import Enums from "@/plugins/Enums"

const numeric = v => (Number.isFinite(Number(v)) ? Number(v) : 0)
const round2 = v => Math.round((numeric(v) + Number.EPSILON) * 100) / 100
const finiteOrNull = v => (Number.isFinite(Number(v)) ? Number(v) : null)

const normalizeSource = s => {
	const v = typeof s === "string" ? s.trim().toLowerCase() : "system"
	return v === "user" || v === "external" ? v : "system"
}

const resolveProductKey = item => {
	return !!item.vdpinsumo ? item.vdpinsumo : item.vdprecip
}

const resolveEffectiveSource = rawSource => normalizeSource(rawSource)

const applyPatch = (target, patch) => {
	Object.keys(patch).forEach(k => {
		if (patch[k] !== undefined) target[k] = patch[k]
	})
}

const computeAutoPrice = ({ base, percent, specialUnit }) => {
	const PS = Enums.PriceSource

	const listUnit = round2(base * (percent / 100))
	const autoSourceEnum =
		specialUnit != null
			? PS.Special.value
			: percent !== 100
				? PS.List.value
				: PS.Base.value

	const autoUnit =
		autoSourceEnum === PS.Special.value ? specialUnit ?? 0 : autoSourceEnum === PS.List.value ? listUnit : base

	let counterfactual = null
	if (autoSourceEnum === PS.Special.value) {
		counterfactual = {
			title: "Sem preço especial",
			hint: percent !== 100 ? "Voltaria para o preço de tabela" : "Voltaria para o preço padrão",
			unit: percent !== 100 ? listUnit : base,
		}
	} else if (autoSourceEnum === PS.List.value) {
		counterfactual = {
			title: "Sem tabela de preços",
			hint: "Voltaria para o preço padrão",
			unit: base,
		}
	}

	return { listUnit, autoSourceEnum, autoUnit, counterfactual }
}

export const buildPricingPatch = (pricingCtx, item) => {
	const PS = Enums.PriceSource

	const percent = numeric(pricingCtx?.priceList?.cv6percent) || 100
	const base = round2(item?.in1preco)

	const specialRaw = pricingCtx?.specialPrices?.[item?.vdpinsumo]?.inapreco
	const specialUnit = specialRaw != null && Number.isFinite(Number(specialRaw)) ? round2(specialRaw) : null

	const { listUnit, autoSourceEnum, autoUnit, counterfactual } = computeAutoPrice({
		base,
		percent,
		specialUnit,
	})

	const priceSource = resolveEffectiveSource(item?.priceSource)

	const appliedPriceEnum =
		priceSource === "user"
			? PS.Manual.value
			: priceSource === "external"
				? PS.Restored.value
				: autoSourceEnum

	return {
		vdpvlunit: priceSource === "system" ? autoUnit : undefined,

		priceSourceEnum: numeric(appliedPriceEnum),

		pricingMeta: {
			base,
			percent,
			listUnit,
			specialUnit,
			autoSourceEnum,
			autoUnit,
			counterfactual,
		},
	}
}

const computeAutoCommission = ({ baseComis, com }) => {
	const CS = Enums.CommissionSource

	const autoRuleEnumValue = com ? numeric(com.enumValue) : CS.None.value
	const autoComis = round2(com ? com.veacomis : baseComis)

	return { autoRuleEnumValue, autoComis }
}

export const buildCommissionPatch = (pricingCtx, item) => {
	const CS = Enums.CommissionSource

	const baseComis = round2(item?.in1comis)
	const produtoKey = resolveProductKey(item)
	const com = pricingCtx?.comissions?.[produtoKey] || null
	const { autoRuleEnumValue, autoComis } = computeAutoCommission({ baseComis, com })

	const commissionSource = resolveEffectiveSource(item?.commissionSource)

	const appliedCommissionEnum =
		commissionSource === "user"
			? CS.Manual.value
			: commissionSource === "external"
				? CS.Restored.value
				: autoRuleEnumValue

	const nextVdpcomis = commissionSource === "system" ? autoComis : round2(item?.vdpcomis)

	const liquido = finiteOrNull(item?.vdpvltotal)
	const vdpcomisValue =
		liquido != null ? round2(liquido * (nextVdpcomis / 100)) : finiteOrNull(item?.vdpcomisValue)

	return {
		vdpcomis: commissionSource === "system" ? autoComis : undefined,

		comissionEnumValue: numeric(appliedCommissionEnum),

		vdpcomisValue: vdpcomisValue != null ? vdpcomisValue : undefined,

		commissionMeta: {
			baseComis,
			autoComis,
			autoRuleEnumValue,
			liquido,
			appliedComis: nextVdpcomis,
			appliedEnumValue: numeric(appliedCommissionEnum),
		},
	}
}

export const applyPricingAndCommission = (pricingCtx, item) => {
	const pricingPatch = buildPricingPatch(pricingCtx, item)
	const commissionPatch = buildCommissionPatch(pricingCtx, item)

	applyPatch(item, pricingPatch)
	applyPatch(item, commissionPatch)
}
